/* eslint-disable camelcase */
import * as React from 'react';
import { Button } from '@rocketmakers/armstrong';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { BackButton } from '../../components/buttons/back-button';
import { routePaths } from '../../routes';
import { useRoutePaths } from '../../../../shared/context/paths';
import { DeviceDescriptor } from './device-descriptor';
import { useBee } from '../../../../shared/hooks/useBee';
import {
  CTMeterIem3100,
  CTMeter_SCHNEIDER_IEM3455,
  DeviceId,
  HeatFlowMeterSharky,
  HeatingControlHeatPump,
  InlineMeterIem2055,
  InlineMeterIem2155,
  RoomDialWrf06,
  RoomSensorMastertherm,
  RoomSensorWrf04,
  RoomSensorWrf06,
  TemperatureSensorNtctf25,
} from '../../../../../frontend/shared/api-clients/_api';
import {
  PeripheralHealthCheckReason,
  PeripheralHealthCheckStatus,
} from '../../../../../backend/shared/types/peripheralHealthCheck';
import { DeviceType } from '../../../../../backend/shared/api-clients/_api/config';
import './testing-peripherals.scss';
import { hasCompleteParams, useBeeConfig } from '../../hooks/beeConfig';
import { peripheralDeviceTypes, formatPeripheralDescription } from '../../../../shared/helpers/peripheralHelpers';

export type RoutePathsParams = {
  deviceId?: DeviceId;
};

export const HEALTHCHECK_POLL_DELAY = 5000;

export const TestingPeripheralsView: React.FunctionComponent = () => {
  const { beeInfo, findBee, loading } = useBee();
  const { getDevicesByType } = useBeeConfig();
  const { deviceId } = useParams<RoutePathsParams>();
  const { push } = useRoutePaths();

  const handleContinue = () => {
    push(routePaths.complete.root);
  };

  const handleRefresh = () => {
    findBee({ deviceId });
  };

  const deviceImages = {
    [DeviceType.IEM2055]: require('../../../assets/images/installer/peripherals/inline-meter.png'),
    [DeviceType.IEM2155]: require('../../../assets/images/installer/peripherals/inline-meter.png'),
    [DeviceType.IEM3100]: require('../../../assets/images/installer/peripherals/schneider-iem3455-electricity-meter.png'),
    [DeviceType.SCHNEIDER_IEM3455]: require('../../../assets/images/installer/peripherals/schneider-iem3455-electricity-meter.png'),
    [DeviceType.MASTERTHERM]: require('../../../assets/images/installer/peripherals/mastertherm.png'),
    [DeviceType.SHARKY]: require('../../../assets/images/installer/peripherals/sharky.png'),
    [DeviceType.WRF04]: require('../../../assets/images/installer/peripherals/thermokon-wrf04.png'),
    [DeviceType.WRF06]: require('../../../assets/images/installer/peripherals/thermokon-wrf06.png'),
    [DeviceType.REGIN]: require('../../../assets/images/installer/peripherals/regin-co2-sensor.png'),
    [DeviceType.NOVOS3]: require('../../../assets/images/installer/peripherals/novos-3-co2-sensor.jpg'),
    [DeviceType.NTCTF25]: require('../../../assets/images/installer/peripherals/ntctf25.jpg'),
  };

  type DeviceWithZoneID =
    | HeatingControlHeatPump
    | RoomSensorMastertherm
    | RoomSensorWrf04
    | RoomDialWrf06
    | RoomSensorWrf06
    | TemperatureSensorNtctf25;

  type DeviceWithoutZoneID =
    | InlineMeterIem2055
    | InlineMeterIem2155
    | CTMeterIem3100
    | CTMeter_SCHNEIDER_IEM3455
    | HeatFlowMeterSharky;

  const peripherals = getDevicesByType<DeviceWithZoneID | DeviceWithoutZoneID>(peripheralDeviceTypes).filter(
    peripheral => hasCompleteParams(peripheral)
  );

  type DisplayPeripheralHealthCheck = {
    status: PeripheralHealthCheckStatus;
    reason: PeripheralHealthCheckReason | null;
    img: string;
    text: string;
  };

  const initialPeripheralHealthChecks = React.useMemo(
    () =>
      peripherals.reduce<{ string?: DisplayPeripheralHealthCheck }>((acc, peripheral) => {
        const isZonePeripheral =
          peripheral.deviceType === DeviceType.WRF06 ||
          peripheral.deviceType === DeviceType.WRF04 ||
          peripheral.deviceType === DeviceType.REGIN ||
          peripheral.deviceType === DeviceType.NOVOS3 ||
          peripheral.deviceType === DeviceType.MASTERTHERM ||
          peripheral.deviceType === DeviceType.NTCTF25;

        const peripheralWithZoneId = peripheral as DeviceWithZoneID;
        const peripheralWithoutZoneId = peripheral as DeviceWithoutZoneID;

        const key = isZonePeripheral
          ? `${peripheralWithZoneId.deviceType} - ${peripheralWithZoneId.params.zone_id}`
          : `${peripheralWithoutZoneId.deviceType} - ${peripheralWithoutZoneId.params.address}_${peripheralWithoutZoneId.params.channel}`;

        const text = isZonePeripheral
          ? formatPeripheralDescription(peripheralWithZoneId.params.zone_id ?? '', peripheralWithZoneId.deviceType)
          : formatPeripheralDescription(
              `${peripheralWithoutZoneId.params.address}_${peripheralWithoutZoneId.params.channel}`,
              peripheralWithoutZoneId.deviceType
            );

        acc[key] = {
          status: PeripheralHealthCheckStatus.PENDING,
          reason: null,
          img:
            deviceImages[peripheral.deviceType] ??
            require('../../../assets/images/installer/peripherals/thermokon-wrf04.png'),
          text,
        };

        return acc;
      }, {}),
    [peripherals]
  );

  const displayPeripheralHealthChecks = React.useMemo<{ string?: DisplayPeripheralHealthCheck }>(() => {
    const checks = {
      ...initialPeripheralHealthChecks,
    };

    const peripheralHealthChecks = beeInfo?.peripheralHealthChecks ?? [];

    for (const check of peripheralHealthChecks) {
      checks[`${check.device_type} - ${check.zone_id}`] = {
        status: check.status,
        reason: check.reason,
        img:
          deviceImages[check.device_type] ??
          require('../../../assets/images/installer/peripherals/thermokon-wrf04.png'),
        text: formatPeripheralDescription(check.zone_id, check.device_type),
      };
    }

    return checks;
  }, [beeInfo?.peripheralHealthChecks, initialPeripheralHealthChecks]);

  const allDevicesConnected = Object.values(displayPeripheralHealthChecks).every(
    check => check.status === PeripheralHealthCheckStatus.PASSED
  );

  useEffect(() => {
    const pollForPeripherals = setInterval(() => {
      if (Object.values(displayPeripheralHealthChecks).length > 0 && allDevicesConnected) {
        clearInterval(pollForPeripherals);
      } else {
        handleRefresh();
      }
    }, HEALTHCHECK_POLL_DELAY);
    return () => clearInterval(pollForPeripherals);
  }, []);

  const overallTestingSummary = React.useMemo(() => {
    if (
      (loading && Object.values(displayPeripheralHealthChecks).length === 0) ||
      Object.values(displayPeripheralHealthChecks).some(check => check.status === PeripheralHealthCheckStatus.PENDING)
    ) {
      return (
        <>
          <p>
            The BEE is communicating with the peripheral devices, this will determine if the BEE has been installed
            correctly.
          </p>
          <div>
            <strong>Please wait, this may take a few minutes.</strong>
          </div>
        </>
      );
    }
    let message = {
      class: PeripheralHealthCheckStatus.PASSED,
      text: 'All devices are connected.',
    };
    if (
      Object.values(displayPeripheralHealthChecks).some(check => check.reason === PeripheralHealthCheckReason.TIMEOUT)
    ) {
      message = {
        class: PeripheralHealthCheckStatus.FAILED,
        text: 'Some devices failed to respond. Please check configuration.',
      };
    } else if (
      Object.values(displayPeripheralHealthChecks).some(check => check.status === PeripheralHealthCheckStatus.FAILED)
    ) {
      message = {
        class: PeripheralHealthCheckStatus.FAILED,
        text: 'Some devices responded with errors. Please check configuration.',
      };
    }

    return (
      <>
        <div>
          <strong className={`testing-peripherals__summary--${message.class}`}>{message.text}</strong>
        </div>
      </>
    );
  }, [loading, displayPeripheralHealthChecks]);

  return (
    <div className="testing-peripherals">
      <BackButton />
      <h1>Testing Peripherals</h1>
      <div data-qa="summary-message" className={"testing-peripherals__summary"}>
        {overallTestingSummary}
      </div>

      <div data-qa="device-descriptors">
        {Object.entries(displayPeripheralHealthChecks).map(([key, device]) => (
          <DeviceDescriptor
            key={key}
            loading={loading}
            status={device.status}
            reason={device.reason}
            img={device.img}
            text={device.text}
          />
        ))}
      </div>

      <div className="row">
        <Button
          className="continue-button btn-full btn-large"
          onClick={handleRefresh}
          disabled={allDevicesConnected}
          data-qa="refresh-button"
        >
          Refresh status
        </Button>
      </div>

      <div className="row">
        <Button
          className="continue-button btn-full btn-large"
          disabled={!allDevicesConnected}
          onClick={handleContinue}
          data-qa="continue-button"
        >
          Done
        </Button>
      </div>
    </div>
  );
};
